import React, { useState, useEffect, useRef, useCallback } from 'react';
import { apiPost, apiPut } from '../utils/apiUtils';
import { motion, AnimatePresence } from 'framer-motion';
import { FiClock, FiAlertTriangle, FiCheckCircle, FiChevronLeft, FiChevronRight, FiCheck, FiSend, FiMaximize, FiShield, FiArrowLeft, FiAlignLeft, FiBookOpen } from 'react-icons/fi';
import Editor from '@monaco-editor/react';
// import CodeEditorPanel from './CodeEditorPanel';
import ProblemDescriptionPanel from './ProblemDescriptionPanel';
import TestCodeEditorPanel from './TestCodeEditorPanel';

/**
 * @file TestAttempt.jsx
 * @description Main component for the student test-taking experience.
 *              Handles the test session, anti-cheat proctoring, MCQ/Coding layouts,
 *              real-time server syncing, and final submission.
 * @module components/TestAttempt
 */

/* ══════════════════════════════════════════════════════════════
 * MAIN COMPONENT
 * ══════════════════════════════════════════════════════════════ */

/**
 * @component TestAttempt
 * @description Renders the proctored test environment for students.
 * 
 * @param {Object} props
 * @param {Object} props.test - The active test data object.
 * @param {Function} props.onComplete - Callback fired when the test is successfully submitted.
 * @returns {JSX.Element} The rendered Test Attempt view.
 */
const TestAttempt = ({ test, onComplete }) => {
    // ── Component State ──
    const [phase, setPhase] = useState('pre'); // 'pre' | 'running' | 'submitted'
    const [loading, setLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    const [submissionId, setSubmissionId] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [codeQuestions, setCodeQuestions] = useState([]); // Array of code-based questions
    const [answers, setAnswers] = useState({}); // { mcq_id: selected_index }
    const [codeAnswers, setCodeAnswers] = useState({}); // { test_question_id: submission_status_string }
    const [codePersistence, setCodePersistence] = useState({}); // { test_question_id: source_code_string }
    const [codeLanguages, setCodeLanguages] = useState({}); // { test_question_id: language_id }

    // UI Navigation State
    const [currentSection, setCurrentSection] = useState('mcq'); // 'mcq' | 'code'
    const [currentQ, setCurrentQ] = useState(0);

    const [timeLeft, setTimeLeft] = useState(0); // seconds
    const [violations, setViolations] = useState(0);
    const [violationLogs, setViolationLogs] = useState([]);
    const [showWarning, setShowWarning] = useState(false);
    const [warningMsg, setWarningMsg] = useState('');

    const [result, setResult] = useState(null);
    const [showConfirmSubmit, setShowConfirmSubmit] = useState(false);
    const [syncingState, setSyncingState] = useState({}); // { [questionId]: 'idle' | 'saving' | 'saved' | 'error' }

    // Layout states for Code Test Panel height resizing
    const [codePanelOpen, setCodePanelOpen] = useState(false);
    const [codePanelMinimized, setCodePanelMinimized] = useState(false);

    const timerRef = useRef(null);
    const submittedRef = useRef(false);
    const violationsRef = useRef(0);
    const sessionStartTimeRef = useRef(null);
    const baseElapsedRef = useRef(0);

    const MAX_VIOLATIONS = 5;

    /* ══════════════════════════════════════════════════════════════
     * EFFECTS & HANDLERS
     * ══════════════════════════════════════════════════════════════ */

    useEffect(() => {
        // Set dynamic page title to the test title
        document.title = `SimpleCode | ${test.title || 'Test Attempt'}`;
        
        return () => {
            // Cleanup handled by parent
            document.title = "SimpleCode";
        };
    }, [test]);

    // Lock body scroll to prevent parent page scrollbar from showing behind the fixed overlay
    useEffect(() => {
        const originalOverflow = document.body.style.overflow;
        const originalHtmlOverflow = document.documentElement.style.overflow;
        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = originalOverflow;
            document.documentElement.style.overflow = originalHtmlOverflow;
        };
    }, []);

    const handleEditorDidMount = (editor, monaco) => {
        monaco.editor.setTheme('vs-dark');
    };

    // ─── Enter Fullscreen ───────────────────────────────────
    /**
     * Requests the browser to enter full-screen mode.
     * Used for proctoring enforcement during the test attempt.
     */
    const enterFullscreen = useCallback(async () => {
        try {
            await document.documentElement.requestFullscreen();
        } catch (e) {
            console.warn('Fullscreen request failed:', e);
        }
    }, []);

    // ─── Save Answer with Retry Logic ──────────────────────
    /**
     * Optimistically saves the user's MCQ answer to local state and syncs with the backend.
     * Retries up to 3 times on failure before flagging an error.
     * 
     * @param {number} questionId - The ID of the MCQ question.
     * @param {number|null} optionIndex - The index of the selected option, or null to clear.
     * @param {number} retryCount - Current retry attempt counter.
     */
    const handleAnswer = useCallback(async (questionId, optionIndex, retryCount = 0) => {
        // Immediately update local UI
        if (optionIndex === null) {
            setAnswers(prev => {
                const newAns = { ...prev };
                delete newAns[questionId];
                return newAns;
            });
        } else {
            setAnswers(prev => ({ ...prev, [questionId]: optionIndex }));
        }

        setSyncingState(prev => ({ ...prev, [questionId]: 'saving' }));

        try {
            const res = await apiPut(`/tests/${test.id}/answer`, {
                submission_id: submissionId,
                mcq_question_id: questionId,
                selected_option_index: optionIndex,
            });

            if (res.ok) {
                setSyncingState(prev => ({ ...prev, [questionId]: 'saved' }));
            } else {
                throw new Error('Save failed');
            }
        } catch (err) {
            console.error(`Save answer error (attempt ${retryCount + 1}):`, err);

            if (retryCount < 2) {
                // Auto-retry up to 2 times after 2 seconds
                setTimeout(() => handleAnswer(questionId, optionIndex, retryCount + 1), 2000);
            } else {
                setSyncingState(prev => ({ ...prev, [questionId]: 'error' }));
            }
        }
    }, [submissionId, test.id]);

    const clearAnswer = (questionId) => {
        handleAnswer(questionId, null);
    };

    // ─── Submit Test ────────────────────────────────────────
    const submitTest = useCallback(async (auto = false, reason = '') => {
        if (submittedRef.current) return;

        // Verify all answers are synced (both MCQ and Code)
        const syncingEntries = Object.entries(syncingState);
        const unsyncedQuestions = syncingEntries.filter(([id, state]) => state === 'saving' || state === 'error');

        if (unsyncedQuestions.length > 0 && !auto) {
            const hasError = syncingEntries.some(([id, state]) => state === 'error');
            const msg = hasError
                ? "Some answers or code submissions failed to sync with our servers. Please wait a moment for them to resolve or try again."
                : "Some answers are still saving. Please wait a moment before submitting.";

            alert(msg);
            return;
        }

        submittedRef.current = true;
        setIsSubmitting(true);

        if (timerRef.current) clearInterval(timerRef.current);

        try {
            if (document.fullscreenElement) {
                await document.exitFullscreen().catch(() => { });
            }
        } catch (e) { /* ignore */ }

        try {
            const response = await apiPost(`/tests/${test.id}/submit`, {
                submission_id: submissionId,
                auto_submitted: auto,
                proctoring_logs: [...violationLogs, ...(reason ? [{ type: reason, time: new Date().toISOString() }] : [])],
            });

            if (!response.ok) {
                const errData = await response.json();
                throw new Error(errData.error || 'Submit failed');
            }

            const data = await response.json();
            setResult(data);
            setPhase('submitted');
        } catch (err) {
            console.error('Submit error:', err);
            // Fallback metrics if API fails
            const totalAttemptedMcq = Object.keys(answers).length;
            const totalAttemptedCode = Object.keys(codePersistence).filter(id => !!codePersistence[id] && codePersistence[id].trim() !== '').length;

            setResult({
                message: auto ? 'Test auto-submitted' : 'Test submitted',
                total_score: '—',
                total_possible: '—',
                questions_attempted: totalAttemptedMcq + totalAttemptedCode,
                total_questions: questions.length + codeQuestions.length
            });
            setPhase('submitted');
        } finally {
            setIsSubmitting(false);
        }
    }, [submissionId, test.id, violationLogs, answers, codeAnswers, questions.length, codeQuestions.length, syncingState, handleAnswer]);

    // ─── Add Violation ──────────────────────────────────────
    const addViolation = useCallback((type) => {
        if (submittedRef.current) return;
        const newCount = violationsRef.current + 1;
        violationsRef.current = newCount;
        setViolations(newCount);
        setViolationLogs(prev => [...prev, { type, time: new Date().toISOString(), count: newCount }]);

        if (newCount >= MAX_VIOLATIONS) {
            submitTest(true, 'max_violations_exceeded');
        } else {
            setWarningMsg(
                type === 'tab_switch'
                    ? `Warning ${newCount}/${MAX_VIOLATIONS}: Tab switching detected!`
                    : type === 'devtools_attempt'
                        ? `Warning ${newCount}/${MAX_VIOLATIONS}: Developer tools blocked!`
                        : `Warning ${newCount}/${MAX_VIOLATIONS}: Fullscreen exited!`
            );
            setShowWarning(true);
            setTimeout(() => setShowWarning(false), 3000);
        }
    }, [submitTest]);

    // ─── Sync Violations to Backend ─────────────────────────
    useEffect(() => {
        if (phase !== 'running' || !submissionId || violationLogs.length === 0) return;

        // Sync the latest violations to the backend so they don't reset on refresh
        apiPut(`/tests/${test.id}/heartbeat`, {
            submission_id: submissionId,
            proctoring_logs: violationLogs
        }).catch(err => console.error('Failed to sync violations:', err));
    }, [violationLogs, submissionId, phase, test.id]);

    // ─── Proctoring Listeners ───────────────────────────────
    useEffect(() => {
        if (phase !== 'running') return;

        const handleVisibility = () => {
            if (document.hidden) addViolation('tab_switch');
        };

        const handleFullscreen = () => {
            if (!document.fullscreenElement && !submittedRef.current) {
                addViolation('fullscreen_exit');
            }
        };

        const handleKeydown = (e) => {
            // F12
            if (e.key === 'F12') {
                e.preventDefault();
                addViolation('devtools_attempt');
            }
            // Ctrl+Shift+I or Ctrl+Shift+J (DevTools)
            if (e.ctrlKey && e.shiftKey && (e.key === 'I' || e.key === 'i' || e.key === 'J' || e.key === 'j')) {
                e.preventDefault();
                addViolation('devtools_attempt');
            }
            // Ctrl+U (View Source)
            if (e.ctrlKey && (e.key === 'U' || e.key === 'u')) {
                e.preventDefault();
                addViolation('devtools_attempt');
            }
        };

        document.addEventListener('visibilitychange', handleVisibility);
        document.addEventListener('fullscreenchange', handleFullscreen);
        document.addEventListener('keydown', handleKeydown);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibility);
            document.removeEventListener('fullscreenchange', handleFullscreen);
            document.removeEventListener('keydown', handleKeydown);
        };
    }, [phase, addViolation]);

    // ─── UI Integrity: Reset Panel States on Question Change ─
    useEffect(() => {
        setCodePanelOpen(false);
        setCodePanelMinimized(false);
    }, [currentQ, currentSection]);


    // ─── Timer & Heartbeat ──────────────────────────────────
    useEffect(() => {
        if (phase !== 'running' || timeLeft <= 0) return;

        timerRef.current = setInterval(() => {
            const now = Date.now();
            const sessionElapsed = Math.floor((now - sessionStartTimeRef.current) / 1000);
            const totalElapsed = baseElapsedRef.current + sessionElapsed;

            const durationSec = (test?.duration_minutes || 0) * 60;
            const remaining = Math.max(durationSec - totalElapsed, 0);

            setTimeLeft(remaining);

            if (remaining <= 0) {
                clearInterval(timerRef.current);
                submitTest(true, 'timer_expired');
            }
        }, 1000);

        return () => clearInterval(timerRef.current);
    }, [phase, submitTest, test]);

    useEffect(() => {
        if (phase !== 'running' || !submissionId) return;

        const hbInterval = setInterval(() => {
            const now = Date.now();
            const sessionElapsed = Math.floor((now - sessionStartTimeRef.current) / 1000);
            const totalElapsed = baseElapsedRef.current + sessionElapsed;

            apiPut(`/tests/${test.id}/heartbeat`, {
                submission_id: submissionId,
                elapsed_seconds: totalElapsed
            }).catch(err => console.error('Heartbeat failed:', err));
        }, 10000);

        return () => clearInterval(hbInterval);
    }, [phase, submissionId, test]);

    // ─── Start Test ─────────────────────────────────────────
    const handleStart = async () => {
        setLoading(true);
        setError('');

        try {
            const response = await apiPost(`/tests/${test.id}/start`, {});
            if (!response.ok) {
                const errData = await response.json();
                throw new Error(errData.error || 'Failed to start test');
            }

            const data = await response.json();
            setSubmissionId(data.submission_id);
            setQuestions(data.mcq_questions || []);
            setCodeQuestions(data.code_questions || []);

            // Set initial active section correctly
            if (data.mcq_questions && data.mcq_questions.length > 0) {
                setCurrentSection('mcq');
            } else if (data.code_questions && data.code_questions.length > 0) {
                setCurrentSection('code');
            }

            // Restore existing answers if resuming
            if (data.resumed) {
                if (data.existing_responses) {
                    const restored = {};
                    const initialSync = {};
                    data.existing_responses.forEach(r => {
                        const qId = r.mcq_question_id;
                        restored[qId] = r.selected_option_index;
                        initialSync[qId] = 'saved';
                    });
                    setAnswers(restored);
                    setSyncingState(initialSync);
                }

                if (data.existing_code_responses) {
                    const restoredCodeStatus = {};
                    const restoredCodePersistence = {};
                    const restoredCodeLanguages = {};

                    console.log('🔍 Resuming test: Found existing code responses:', data.existing_code_responses.length);

                    data.existing_code_responses.forEach(r => {
                        // Defensive matching: Try matching question_id (global) to cq.question_id
                        const targetId = String(r.question_id);
                        const codeQuestion = data.code_questions.find(cq => String(cq.question_id) === targetId);

                        if (codeQuestion) {
                            console.log(`✅ Matched code for Question ID ${targetId} to Link ID ${codeQuestion.id}`);
                            restoredCodeStatus[codeQuestion.id] = r.status || 'attempted';
                            restoredCodePersistence[codeQuestion.id] = r.source_code;
                            if (r.language_id) restoredCodeLanguages[codeQuestion.id] = r.language_id;
                        } else {
                            console.warn(`⚠️ Could not match existing code response for question_id ${targetId} to any test questions.`);
                        }
                    });

                    setCodeAnswers(restoredCodeStatus);
                    setCodePersistence(restoredCodePersistence);
                    setCodeLanguages(restoredCodeLanguages);

                    setSyncingState(prev => ({
                        ...prev,
                        ...Object.keys(restoredCodePersistence).reduce((acc, key) => ({ ...acc, [`code_${key}`]: 'saved' }), {})
                    }));
                }
            }

            // Initialize Timer variables tracking accumulated time
            const baseElapsed = typeof data.elapsed_seconds === 'number' ? data.elapsed_seconds : 0;
            baseElapsedRef.current = baseElapsed;
            sessionStartTimeRef.current = Date.now();

            const durationSec = (data.test?.duration_minutes || test.duration_minutes || 0) * 60;
            setTimeLeft(Math.max(durationSec - baseElapsed, 0));

            // Add violation if resuming (session was interrupted)
            if (data.resumed) {
                const historicalLogs = data.proctoring_logs || [];
                const newCount = historicalLogs.length + 1; // Existing violations + the interruption that caused resumption

                violationsRef.current = newCount;
                setViolations(newCount);
                setViolationLogs([
                    ...historicalLogs,
                    { type: 'session_interrupted', time: new Date().toISOString(), count: newCount }
                ]);
            }

            await enterFullscreen();
            setPhase('running');
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };


    // ─── Format Timer ───────────────────────────────────────
    const formatTime = (secs) => {
        const m = Math.floor(secs / 60);
        const s = secs % 60;
        return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    };

    const currentQuestion = questions[currentQ];
    const answeredCount = Object.keys(answers).length;

    // ═══════════════════════════════════════════════════════
    // PRE-START SCREEN
    // ═══════════════════════════════════════════════════════
    if (phase === 'pre') {
        return (
            <div className="fixed inset-0 bg-accent z-[100] flex items-center justify-center p-4">
                <div className="max-w-lg w-full bg-white border border-gray-300 rounded p-8 space-y-6">
                    <div className="text-center">
                        <h2 className="text-2xl font-bold text-foreground mb-2">{test.title}</h2>
                        <p className="text-muted-foreground text-sm">{test.description}</p>
                    </div>

                    <div className="space-y-3 bg-accent p-4 border border-border rounded text-sm">
                        <h3 className="font-semibold text-foreground mb-2 text-md">Test Rules</h3>
                        <div className="space-y-2 text-muted-foreground text-left">
                            <p>•  Duration: <span className="text-foreground font-medium">{test.duration_minutes} minutes</span></p>
                            <p>•  Fullscreen mode is <span className="text-amber-600 font-medium">mandatory</span></p>
                            <p>•  Switching tabs or exiting fullscreen counts as a <span className="text-red-600 font-medium">violation</span></p>
                            <p>•  After <span className="text-red-600 font-bold">5 violations</span>, the test will be auto-submitted</p>
                            <p>•  If time runs out, the test will auto-submit</p>
                            <p>•  Answers and code drafts are saved automatically</p>
                        </div>
                    </div>

                    {error && (
                        <div className="text-red-600 text-sm p-3 bg-red-50 border border-red-200 rounded flex items-center justify-center space-x-2">
                            <span>{error} !!</span>
                        </div>
                    )}

                    <button
                        onClick={handleStart}
                        disabled={loading}
                        className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded transition-colors disabled:opacity-50 flex items-center justify-center space-x-2"
                    >
                        {loading ? (
                            <><span className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin"></span><span>Starting...</span></>
                        ) : (
                            <><FiMaximize /><span>Enter Fullscreen & Start Test</span></>
                        )}
                    </button>

                    <button onClick={onComplete} className="w-full py-2 text-muted-foreground hover:text-emerald-600 text-sm transition-colors">
                        ← Go Back
                    </button>
                </div>
            </div>
        );
    }

    // ═══════════════════════════════════════════════════════
    // SUBMITTED SCREEN
    // ═══════════════════════════════════════════════════════
    if (phase === 'submitted') {
        return (
            <div className="fixed inset-0 bg-accent z-[100] flex items-center justify-center p-4">
                <div className="max-w-md w-full bg-white border border-gray-300 rounded p-8 text-center space-y-3">
                    <div className='flex flex-row items-center justify-center gap-2'>
                        <h2 className="text-2xl font-bold text-foreground">Test Completed</h2>
                        {/* <FiCheck className="text-3xl text-emerald-600" /> */}
                    </div>
                    <p className="text-muted-foreground text-sm">{result?.message}</p>

                    <div className="space-y-2">
                        {/* Centered Summary Area */}
                        <div className="bg-accent p-4 text-center flex flex-col items-center justify-center rounded">
                            <h2 className="text-5xl font-black text-foreground flex items-baseline justify-center">
                                {result?.questions_attempted || 0}
                                <span className="text-muted-foreground text-2xl font-bold ml-2">/ {result?.total_questions || 0}</span>
                            </h2>
                            <p className="text-muted-foreground text-xs mt-3 uppercase tracking-widest font-bold">Questions Attempted</p>
                        </div>

                        {/* MCQ Breakdown */}
                        {result?.mcq_total > 0 && test.type === 'mixed' && (
                            <div className="bg-accent p-4 flex justify-between items-center text-xs uppercase tracking-wider font-bold rounded">
                                <span className="text-muted-foreground">MCQ Section</span>
                                <span className="text-foreground">{result?.mcq_attempted || 0} / {result?.mcq_total || 0} Answered</span>
                            </div>
                        )}

                        {/* Coding Detailed Results */}
                        {result?.code_results && result.code_results.length > 0 && test.type !== 'mcq' && (
                            <div className="space-y-3 pt-2">
                                <p className="text-xs text-muted-foreground uppercase tracking-[0.2em] font-semibold text-left">Coding Performance</p>
                                <div className="space-y-2">
                                    {result.code_results.map((c, i) => {
                                        const isUnattempted = !c.status || c.status === 'Unattempted';
                                        const isAccepted = c.status === 'Accepted';

                                        return (
                                            <div key={i} className={`bg-accent p-4 rounded border-l-4 ${isAccepted ? 'border-emerald-600' : isUnattempted ? 'border-amber-600' : 'border-red-600'}`}>
                                                <div className="flex justify-between items-center">
                                                    <div className="text-left">
                                                        <p className="text-sm font-semibold text-foreground mb-1">Problem {i + 1}</p>
                                                        <div className={`text-xs font-medium tracking-widest py-0.5 inline-block ${isAccepted ? 'text-emerald-600' :
                                                            isUnattempted ? 'text-amber-600' :
                                                                'text-red-600'
                                                            }`}>
                                                            {isUnattempted ? 'Unattempted' : (isAccepted ? 'Accepted' : 'Failed')}
                                                        </div>
                                                    </div>
                                                    <div className="text-right">
                                                        <p className={`text-base font-black ${isUnattempted ? 'text-gray-400' : 'text-emerald-600'}`}>
                                                            {c.passed_test_cases ?? 0} <span className="text-muted-foreground text-xs">/ {c.total_test_cases ?? 0}</span>
                                                        </p>
                                                        <span className="text-xs text-muted-foreground ml-2 font-medium capitalize tracking-widest">test cases passed</span>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>

                    {violations > 0 && (
                        <div className="text-red-600 text-sm flex flex-row items-center gap-2 justify-center">
                            <p>{violations} violation{violations > 1 ? 's' : ''} recorded during the test !!
                            </p>
                        </div>
                    )}

                    <button
                        onClick={onComplete}
                        className="w-full text-foreground font-semibold transition-colors"
                    >
                        <div className="flex flex-row items-center justify-center gap-2 mt-1 hover:text-emerald-600">
                            <FiChevronLeft />
                            Return to Batch
                        </div>
                    </button>
                </div>
            </div>
        );
    }

    // ═══════════════════════════════════════════════════════
    // RUNNING STATE — FULL TEST INTERFACE
    // ═══════════════════════════════════════════════════════
    return (
        <div className="fixed inset-0 bg-accent z-[100] flex flex-col">
            {/* Warning Toast */}
            {showWarning && (
                <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[110] bg-red-600 text-white px-6 py-3 rounded shadow-2xl flex items-center space-x-3 animate-pulse">
                    <FiAlertTriangle className="text-xl flex-shrink-0" />
                    <span className="font-medium text-sm">{warningMsg}</span>
                </div>
            )}

            {/* Top Bar */}
            <div className="relative flex items-center justify-between px-3 py-3 bg-emerald-900 border-b border-emerald-800">

                {/* Left Side */}
                <div className="flex items-center space-x-4">
                    <h1 className="text-gray-100 font-bold text-lg">{test.title}</h1>
                </div>

                {/* Center Logo (True Center) */}
                <div className="absolute left-1/2 -translate-x-1/2">
                    <img src="/logo.png" alt="Logo" className="h-8" />
                </div>

                {/* Right Side */}
                <div className="flex items-center space-x-6">

                    <div className={`flex items-center space-x-1.5 text-xs font-medium ${violations >= 4 ? 'bg-red-600 rounded px-2 py-1 text-white' :
                        violations >= 2 ? 'bg-amber-600 rounded px-2 py-1 text-white' :
                            'bg-emerald-600 rounded px-2 py-1 text-white'
                        }`}>
                        <FiAlertTriangle />
                        <span>{violations}/{MAX_VIOLATIONS}</span>
                    </div>

                    <div className={`flex items-center space-x-2 text-lg font-bold ${timeLeft <= 60 ? 'text-amber-400 animate-pulse' :
                        timeLeft <= 300 ? 'text-white' :
                            'text-white'
                        }`}>
                        <FiClock />
                        <span>{formatTime(timeLeft)}</span>
                    </div>

                    <button
                        onClick={() => setShowConfirmSubmit(true)}
                        className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded transition-colors flex items-center space-x-2"
                    >
                        <FiSend />
                        <span>End Test</span>
                    </button>

                </div>

            </div>

            {/* Main Content */}
            <div className="flex flex-1 min-h-0 overflow-hidden">
                {/* Question Palette (Left Sidebar) */}
                <div className="w-64 bg-accent border-r border-gray-300 p-4 overflow-y-auto custom-scrollbar flex flex-col justify-between">

                    {/* Section I: MCQs */}
                    {questions.length > 0 && (
                        <div>
                            <div
                                className="flex items-center justify-between mb-3 cursor-pointer"
                                onClick={() => { if (currentSection !== 'mcq') { setCurrentSection('mcq'); setCurrentQ(0); } }}
                            >
                                <h3 className={`text-xs font-semibold uppercase tracking-wider transition-colors ${currentSection === 'mcq' ? 'text-muted-foreground' : 'text-muted-foreground'}`}>
                                    {codeQuestions.length > 0 ? 'Section I: MCQ' : 'Questions'}
                                </h3>
                                {currentSection === 'mcq' && codeQuestions.length > 0 && <div className="w-2 h-2 animate-pulse rounded-full bg-red-600" />}
                            </div>

                            <div className="grid grid-cols-5 gap-2">
                                {questions.map((q, idx) => {
                                    const isAnswered = answers[q.id] !== undefined;
                                    const isCurrent = currentSection === 'mcq' && idx === currentQ;
                                    return (
                                        <button
                                            key={q.id}
                                            onClick={() => { setCurrentSection('mcq'); setCurrentQ(idx); }}
                                            className={`w-10 h-10 text-sm font-semibold rounded-full transition-all relative ${isCurrent
                                                ? 'text-emerald-700 border-2 border-emerald-600 bg-emerald-50'
                                                : isAnswered
                                                    ? 'bg-emerald-600 text-white border-transparent'
                                                    : 'bg-white text-foreground border-2 border-gray-300'
                                                }`}
                                        >
                                            {idx + 1}
                                            {/* {isAnswered && (
                                                <div className="absolute -top-1 -right-1 bg-emerald-500 rounded-full p-0.5 shadow-lg">
                                                    <FiCheck className="text-[8px] text-white" />
                                                </div>
                                            )} */}
                                            {syncingState[q.id] === 'saving' && (
                                                <div className="absolute -top-1 -left-1 w-3 h-3 bg-white rounded-full flex items-center justify-center shadow">
                                                    <div className="w-2 h-2 border border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                                                </div>
                                            )}
                                            {syncingState[q.id] === 'error' && (
                                                <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center text-[10px]">!</div>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>

                            <div className="mt-4 space-y-2 text-xs text-muted-foreground">
                                <div className="flex items-center space-x-2">
                                    <span className="w-3 h-3 bg-emerald-600 rounded-full"></span>
                                    <span>Answered ({answeredCount})</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <span className="w-3 h-3 bg-white border-2 border-gray-300 rounded-full"></span>
                                    <span>Unanswered ({questions.length - answeredCount})</span>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Section II: Code Problems */}
                    {codeQuestions.length > 0 && (
                        <div>
                            <div
                                className="flex items-center justify-between mt-5 mb-3 cursor-pointer"
                                onClick={() => { if (currentSection !== 'code') { setCurrentSection('code'); setCurrentQ(0); } }}
                            >
                                <h3 className={`text-xs font-semibold uppercase tracking-wider transition-colors ${currentSection === 'code' ? 'text-muted-foreground' : 'text-muted-foreground'}`}>
                                    {questions.length > 0 ? 'Section II: Code' : 'Questions'}
                                </h3>
                                {currentSection === 'code' && questions.length > 0 && <div className="w-2 h-2 animate-pulse rounded-full bg-red-600" />}
                            </div>

                            <div className="grid grid-cols-5 gap-2">
                                {codeQuestions.map((q, idx) => {
                                    const isAttempted = !!codeAnswers[q.id];
                                    const isCurrent = currentSection === 'code' && idx === currentQ;
                                    return (
                                        <button
                                            key={q.id}
                                            onClick={() => { setCurrentSection('code'); setCurrentQ(idx); }}
                                            className={`w-10 h-10 text-sm font-semibold rounded-full transition-all relative ${isCurrent
                                                ? 'text-emerald-700 border-2 border-emerald-600 bg-emerald-50'
                                                : isAttempted
                                                    ? 'bg-emerald-600 text-white border-transparent'
                                                    : 'bg-white text-foreground border-2 border-gray-300'
                                                }`}
                                        >
                                            {idx + 1}
                                            {/* {codeAnswers[q.id] === 'Accepted' && (
                                                <div className="absolute -top-1 -right-1 bg-emerald-500 rounded-full p-0.5 shadow-lg">
                                                    <FiCheck className="text-[8px] text-white" />
                                                </div>
                                            )} */}
                                            {syncingState[`code_${q.id}`] === 'saving' && (
                                                <div className="absolute -top-1 -left-1 w-3 h-3 bg-white rounded-full flex items-center justify-center shadow">
                                                    <div className="w-2 h-2 border border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                                                </div>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>

                            <div className="mt-4 space-y-2 text-xs text-muted-foreground">
                                <div className="flex items-center space-x-2">
                                    <span className="w-3 h-3 bg-emerald-600 rounded-full"></span>
                                    <span>Attempted ({Object.keys(codeAnswers).length})</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <span className="w-3 h-3 bg-white border-2 border-gray-300 rounded-full"></span>
                                    <span>Unattempted ({codeQuestions.length - Object.keys(codeAnswers).length})</span>
                                </div>
                            </div>

                        </div>
                    )}

                    <div className='mt-auto pt-6'>
                        {/* Persistence Note */}
                        <div className="border-y border-border py-4 mb-4 text-xs text-muted-foreground leading-relaxed italic text-justify">
                            <p>Note: Your latest submitted code or auto-saved draft is preserved even if you refresh or switch questions.</p>
                        </div>
                    </div>

                    <div className='flex items-center justify-center bg-emerald-900 py-3 -mx-4 -mb-4'>
                        <img src="/logo.png" alt="Logo" className="h-8" />
                    </div>
                </div>

                {/* Question Area */}
                <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar">
                    {/* SECTION I: MCQ AREA */}
                    {currentSection === 'mcq' && currentQuestion && (
                        <div className="max-w-3xl mx-auto p-8">
                            <div className="flex items-start justify-between mb-6">
                                <span className="text-sm text-muted-foreground py-1">
                                    Question {currentQ + 1} of {questions.length}
                                </span>
                                <span className="text-sm text-emerald-600 font-semibold">
                                    {currentQuestion.marks} {currentQuestion.marks === 1 ? 'Mark' : 'Marks'}
                                </span>
                            </div>

                            <div className="mb-8 text-left space-y-4">
                                <h2 className="text-xl font-bold text-foreground leading-relaxed">
                                    {currentQuestion.question_text}
                                </h2>

                                {currentQuestion.question_code && (
                                    <div className="flex flex-col rounded border border-neutral-700 bg-neutral-800 overflow-hidden font-mono cursor-not-allowed">
                                        <style>
                                            {`
                                                .test-mcq-code .monaco-editor .view-line, 
                                                .test-mcq-code .monaco-editor .view-lines,
                                                .test-mcq-code .monaco-editor .margin-view-overlays,
                                                .test-mcq-code .monaco-editor .view-content {
                                                    cursor: not-allowed !important;
                                                }
                                            `}
                                        </style>
                                        <div className="bg-neutral-800/50 px-2 py-2 border-b border-neutral-700">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center space-x-3">
                                                    <div className="flex space-x-1.5">
                                                        <div className="w-3 h-3 rounded-full bg-[#ff5f56]"></div>
                                                        <div className="w-3 h-3 rounded-full bg-[#ffbd2e]"></div>
                                                        <div className="w-3 h-3 rounded-full bg-[#27c93f]"></div>
                                                    </div>
                                                    <span className="text-sm text-gray-400 font-mono">
                                                        main.cpp
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="test-mcq-code py-2 cursor-not-allowed" style={{ height: Math.max(85, Math.min(400, (currentQuestion.question_code.split('\n').length * 24) + 42)) + 'px' }}>
                                            <Editor
                                                height="100%"
                                                theme="vs-dark"
                                                defaultLanguage="cpp"
                                                value={currentQuestion.question_code}
                                                onMount={handleEditorDidMount}
                                                options={{
                                                    readOnly: true,
                                                    domReadOnly: true,
                                                    fontSize: 13,
                                                    lineHeight: 24,
                                                    fontFamily: "'Fira Code', 'Cascadia Code', 'Ubuntu Mono', monospace",
                                                    minimap: { enabled: false },
                                                    scrollBeyondLastLine: false,
                                                    lineNumbers: 'on',
                                                    lineNumbersMinChars: 3,
                                                    lineDecorationsWidth: 40,
                                                    renderLineHighlight: 'none',
                                                    scrollbar: {
                                                        verticalScrollbarSize: 6,
                                                        horizontalScrollbarSize: 6,
                                                        useShadows: false
                                                    },
                                                    padding: { top: 16, bottom: 16 },
                                                    folding: false,
                                                    automaticLayout: true,
                                                    contextmenu: false
                                                }}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Status and Action Buttons */}
                            <div className="flex items-center justify-between mb-8 min-h-[42px]">
                                <div className="flex-1"></div>
                                <AnimatePresence mode="wait">
                                    {answers[currentQuestion.id] !== undefined && (
                                        <motion.div
                                            key="clear-selection-container"
                                            initial={{ height: 0, opacity: 0, y: -10, filter: 'blur(4px)' }}
                                            animate={{ height: 'auto', opacity: 1, y: 0, filter: 'blur(0px)' }}
                                            exit={{ height: 0, opacity: 0, y: -10, filter: 'blur(4px)' }}
                                            transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                                            className="flex flex-col items-end space-y-2 ml-4 overflow-hidden"
                                        >
                                            <button
                                                onClick={() => clearAnswer(currentQuestion.id)}
                                                className="px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700 transition-colors bg-red-600 rounded"
                                            >
                                                Clear Selection
                                            </button>

                                            {/* Dynamic Sync Status */}
                                            <div className="flex items-center space-x-2 text-[10px] font-bold uppercase tracking-wider">
                                                {syncingState[currentQuestion.id] === 'saving' && (
                                                    <div className="flex items-center space-x-1.5 text-amber-600">
                                                        <div className="w-2.5 h-2.5 border-2 border-amber-500/30 border-t-amber-500 rounded-full animate-spin"></div>
                                                        <span>Saving...</span>
                                                    </div>
                                                )}
                                                {syncingState[currentQuestion.id] === 'saved' && (
                                                    <div className="flex items-center space-x-1 text-emerald-600">
                                                        <FiCheck className="text-xs" />
                                                        <span>Saved</span>
                                                    </div>
                                                )}
                                                {syncingState[currentQuestion.id] === 'error' && (
                                                    <div className="flex items-center space-x-1.5 text-red-500">
                                                        <div className="w-3.5 h-3.5 bg-red-500 rounded-full flex items-center justify-center text-[8px] text-white">!</div>
                                                        <span>Sync Error</span>
                                                    </div>
                                                )}
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>

                            <div className="grid grid-cols-1 gap-2">
                                {(currentQuestion.options || []).map((opt, optIdx) => {
                                    const isSelected = answers[currentQuestion.id] === optIdx;
                                    return (
                                        <div key={optIdx} className="space-y-2">
                                            <button
                                                onClick={() => handleAnswer(currentQuestion.id, optIdx)}
                                                className={`w-full flex items-center px-4 py-3 text-sm rounded transition-all border-2 border-transparent ${isSelected
                                                    ? 'bg-emerald-600 text-white'
                                                    : 'bg-white text-slate-700 hover:bg-gray-200'
                                                    }`}
                                            >
                                                <div className={`w-5 h-5 rounded-full flex items-center justify-center mr-3 shrink-0 ${isSelected ? 'bg-white' : 'bg-gray-300'
                                                    }`}>
                                                    {isSelected ? (
                                                        <FiCheck className="w-3 h-3 text-emerald-600" strokeWidth={5} />
                                                    ) : (
                                                        <span className="text-[10px] font-bold text-gray-600"></span>
                                                    )}
                                                </div>
                                                <span className="flex-1 font-medium text-left">{opt}</span>
                                            </button>

                                            {currentQuestion.option_codes && currentQuestion.option_codes[optIdx] && (
                                                <div className="mb-2">
                                                    <div className="flex flex-col rounded border border-neutral-700 bg-neutral-800 overflow-hidden font-mono cursor-not-allowed">
                                                        <div className="bg-neutral-800/50 px-2 py-2 border-b border-neutral-700">
                                                            <div className="flex items-center justify-between">
                                                                <div className="flex items-center space-x-3">
                                                                    <div className="flex space-x-1.5">
                                                                        <div className="w-3 h-3 rounded-full bg-[#ff5f56]"></div>
                                                                        <div className="w-3 h-3 rounded-full bg-[#ffbd2e]"></div>
                                                                        <div className="w-3 h-3 rounded-full bg-[#27c93f]"></div>
                                                                    </div>
                                                                    <span className="text-sm text-gray-400 font-mono">
                                                                        option_{String.fromCharCode(97 + optIdx)}.cpp
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="test-mcq-code py-2 cursor-not-allowed" style={{ height: Math.max(85, Math.min(150, (currentQuestion.option_codes[optIdx].split('\n').length * 24) + 42)) + 'px' }}>
                                                            <Editor
                                                                height="100%"
                                                                theme="vs-dark"
                                                                defaultLanguage="cpp"
                                                                value={currentQuestion.option_codes[optIdx]}
                                                                onMount={handleEditorDidMount}
                                                                options={{
                                                                    readOnly: true,
                                                                    domReadOnly: true,
                                                                    fontSize: 13,
                                                                    lineHeight: 24,
                                                                    fontFamily: "'Fira Code', 'Cascadia Code', 'Ubuntu Mono', monospace",
                                                                    minimap: { enabled: false },
                                                                    scrollBeyondLastLine: false,
                                                                    lineNumbers: 'on',
                                                                    lineNumbersMinChars: 3,
                                                                    lineDecorationsWidth: 40,
                                                                    renderLineHighlight: 'none',
                                                                    scrollbar: {
                                                                        verticalScrollbarSize: 6,
                                                                        horizontalScrollbarSize: 6,
                                                                        useShadows: false
                                                                    },
                                                                    padding: { top: 16, bottom: 16 },
                                                                    folding: false,
                                                                    automaticLayout: true,
                                                                    contextmenu: false
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Navigation */}
                            <div className="flex justify-between mt-10">
                                <button
                                    onClick={() => setCurrentQ(Math.max(0, currentQ - 1))}
                                    disabled={currentQ === 0}
                                    className="px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white text-sm font-medium rounded border border-border transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                                >
                                    ← Previous
                                </button>

                                {currentQ < questions.length - 1 ? (
                                    <button
                                        onClick={() => setCurrentQ(currentQ + 1)}
                                        className="px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white text-sm font-medium rounded border border-border transition-colors"
                                    >
                                        Next →
                                    </button>
                                ) : codeQuestions.length > 0 ? (
                                    <button
                                        onClick={() => {
                                            setCurrentSection('code');
                                            setCurrentQ(0);
                                        }}
                                        className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded transition-colors flex items-center space-x-2"
                                    >
                                        <span>Next Section →</span>
                                    </button>
                                ) : (
                                    <button
                                        onClick={() => setShowConfirmSubmit(true)}
                                        className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded transition-colors flex items-center space-x-2"
                                    >
                                        <FiSend />
                                        <span>End Test</span>
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {/* SECTION II: CODE AREA */}
                    {currentSection === 'code' && codeQuestions[currentQ] && (
                        <div className="w-full flex flex-col relative" style={{ height: 'calc(100vh - 61px)' }}>
                            <div className="flex flex-col lg:flex-row" style={{ height: codePanelOpen ? (codePanelMinimized ? 'calc(100% - 3rem)' : 'calc(100% - 40vh)') : '100%', transition: 'height 0.3s ease' }}>
                                {/* Problem Description Pane (Left) */}
                                <div className="w-full lg:w-1/2 border-r border-border flex flex-col relative overflow-hidden bg-accent">
                                    <div className="flex-1 overflow-y-auto">
                                        <ProblemDescriptionPanel
                                            question={codeQuestions[currentQ].question}
                                            sampleTestCases={codeQuestions[currentQ].question.test_cases?.filter(tc => tc.is_sample)}
                                            isAuthenticated={true}
                                            navigate={() => { }}
                                            isTestMode={true}
                                            isSolved={codeAnswers[codeQuestions[currentQ].id] === 'Accepted' || codeAnswers[codeQuestions[currentQ].id] === 'completed'}
                                            marks={codeQuestions[currentQ].marks}
                                        />
                                    </div>

                                    {/* Code Section Navigation */}
                                    <div className="px-4 pt-3 pb-2.5 border-t border-gray-300 bg-accent flex justify-between items-center">
                                        <button
                                            onClick={() => {
                                                if (currentQ > 0) {
                                                    setCurrentQ(currentQ - 1);
                                                } else {
                                                    setCurrentSection('mcq');
                                                    setCurrentQ(questions.length - 1);
                                                }
                                            }}
                                            className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white text-xs font-semibold rounded border border-border transition-colors"
                                        >
                                            ← Previous
                                        </button>

                                        {currentQ < codeQuestions.length - 1 ? (
                                            <button
                                                onClick={() => setCurrentQ(currentQ + 1)}
                                                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white text-xs font-semibold rounded border border-border transition-colors"
                                            >
                                                Next →
                                            </button>
                                        ) : (
                                            <button
                                                onClick={() => setShowConfirmSubmit(true)}
                                                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded transition-colors flex items-center space-x-2"
                                            >
                                                <FiSend />
                                                <span>Submit Test</span>
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* Code Editor Pane (Right) */}
                                <div className="w-full lg:w-1/2 flex flex-col bg-neutral-800 relative">
                                    {/* Code Editor tuned specifically for exams */}
                                    <TestCodeEditorPanel
                                        key={codeQuestions[currentQ].id}
                                        question={codeQuestions[currentQ].question}
                                        testQuestionId={codeQuestions[currentQ].id}
                                        submissionId={submissionId}
                                        testId={test.id}
                                        initialCode={codePersistence[codeQuestions[currentQ].id]}
                                        restoredLanguageId={codeLanguages[codeQuestions[currentQ].id]}
                                        onStatusChange={(statusStr) => {
                                            setCodeAnswers(prev => ({ ...prev, [codeQuestions[currentQ].id]: statusStr }));
                                        }}
                                        onPanelChange={(isOpen, isMinimized) => {
                                            setCodePanelOpen(isOpen);
                                            setCodePanelMinimized(isMinimized);
                                        }}
                                        onCodeChange={(newCode) => {
                                            setCodePersistence(prev => ({ ...prev, [codeQuestions[currentQ].id]: newCode }));
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Fullscreen Re-entry Button (shows when not in fullscreen) */}
            {phase === 'running' && (
                <FullscreenReminder onReEnter={enterFullscreen} />
            )}

            {/* Submitting Loading Overlay */}
            {isSubmitting && (
                <div className="fixed inset-0 bg-white/90 z-[130] flex flex-col items-center justify-center p-4 backdrop-blur-sm">
                    <div className="w-12 h-12 border-4 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin mb-6"></div>
                    <h2 className="text-xl font-bold text-foreground mb-2">Submitting Test...</h2>
                    <p className="text-muted-foreground text-sm">Please do not close this window</p>
                </div>
            )}

            {/* Custom Submit Confirmation Modal */}
            {showConfirmSubmit && (
                <div className="fixed inset-0 bg-emerald-950/50 backdrop-blur-md z-[120] flex items-center justify-center p-4">
                    <div className="max-w-md w-full bg-accent rounded shadow-2xl p-6 space-y-6">
                        <div>
                            <h2 className="text-xl font-bold text-foreground mb-2">Submit Test</h2>
                            <p className="text-muted-foreground text-sm leading-relaxed">
                                Are you sure you want to submit your test?
                                <br />
                                You have answered <span className="text-emerald-600 font-bold">{answeredCount + Object.keys(codeAnswers).length}</span> out of <span className="text-foreground font-bold">{questions.length + codeQuestions.length}</span> questions.
                            </p>
                        </div>
                        <div className="flex space-x-3 justify-end">
                            <button
                                onClick={() => setShowConfirmSubmit(false)}
                                className="px-5 py-2.5 text-muted-foreground hover:text-foreground text-sm font-medium transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => {
                                    setShowConfirmSubmit(false);
                                    submitTest(false);
                                }}
                                className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded transition-colors flex items-center space-x-2"
                            >
                                <FiSend />
                                <span>Yes, Submit</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// Small sub-component to show fullscreen re-entry prompt
const FullscreenReminder = ({ onReEnter }) => {
    const [show, setShow] = useState(false);

    useEffect(() => {
        const check = () => setShow(!document.fullscreenElement);
        document.addEventListener('fullscreenchange', check);
        check();
        return () => document.removeEventListener('fullscreenchange', check);
    }, []);

    if (!show) return null;

    return (
        <div className="absolute bottom-4 right-4 bg-white border border-amber-300 text-amber-700 px-4 py-3 rounded shadow-lg flex items-center space-x-3 z-[110]">
            <FiMaximize />
            <span className="text-sm font-medium">Not in Full Screen</span>
            <button onClick={onReEnter} className="px-3 py-1 bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold rounded transition-colors">
                Re-enter
            </button>
        </div>
    );
};

export default TestAttempt;
